import { useEffect, useMemo, useRef, useState } from 'react'
import type { ShopCategory } from './EshopCategories'

const norm = (value: string) => value.toLocaleLowerCase('sk').normalize('NFD').replace(/\p{Diacritic}/gu, '')

/** Rozbaľovacie menu s kategóriami e-shopu – produkt môže byť vo viacerých kategóriách naraz. */
export default function ShopCategoryPicker({ categories, value, onChange, warn }: {
  categories: ShopCategory[]
  value: string[]
  onChange: (ids: string[]) => void
  warn?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const close = (event: MouseEvent) => { if (box.current && !box.current.contains(event.target as Node)) setOpen(false) }
    const esc = (event: KeyboardEvent) => { if (event.key === 'Escape') { event.stopPropagation(); setOpen(false) } }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', esc, true)
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc, true) }
  }, [open])

  const byId = useMemo(() => new Map(categories.map(c => [c.id, c])), [categories])
  const label = (category: ShopCategory) => {
    const parent = category.parentId ? byId.get(category.parentId) : undefined
    return parent ? `${parent.name} › ${category.name}` : category.name
  }
  const selected = value.map(id => byId.get(id)).filter((c): c is ShopCategory => Boolean(c))
  const query = norm(search.trim())
  const visible = query
    ? categories.filter(c => norm(label(c)).includes(query))
    : categories

  const toggle = (id: string) => onChange(value.includes(id) ? value.filter(v => v !== id) : [...value, id])

  return <div className={`category-picker ${open ? 'is-open' : ''}`} ref={box}>
    <span className="category-picker-label">Kategórie v e-shope {selected.length > 0 && <small>({selected.length})</small>}</span>
    <div className={`category-picker-control ${warn ? 'is-warning' : ''}`} role="button" tabIndex={0} aria-haspopup="listbox" aria-expanded={open}
      onClick={() => setOpen(o => !o)} onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(o => !o) } }}>
      {selected.length ? selected.map(category => <span className="category-chip" key={category.id}>
        {label(category)}
        <button type="button" aria-label={`Odobrať kategóriu ${category.name}`} onClick={e => { e.stopPropagation(); toggle(category.id) }}>×</button>
      </span>) : <span className="category-picker-placeholder">Vyberte kategórie…</span>}
      <span className="category-picker-caret" aria-hidden="true">▾</span>
    </div>
    {warn && <small className="form-warning">Bez kategórie ho zákazníci v e-shope neuvidia.</small>}
    {open && <div className="category-picker-menu" role="listbox" aria-multiselectable="true">
      {categories.length > 6 && <input className="category-picker-search" autoFocus placeholder="Hľadať kategóriu…" value={search} onChange={e => setSearch(e.target.value)} />}
      {!categories.length && <p className="hint">Zatiaľ nie sú vytvorené žiadne kategórie e-shopu – vytvoríte ich v menu Kategórie.</p>}
      {categories.length > 0 && !visible.length && <p className="hint">Nič sa nenašlo.</p>}
      {visible.map(category => <label key={category.id} className={`category-picker-option ${category.parentId && !query ? 'is-sub' : ''} ${category.parentId ? '' : 'is-main'}`}>
        <input type="checkbox" checked={value.includes(category.id)} onChange={() => toggle(category.id)} />
        <span>{query ? label(category) : category.name}</span>
        {!category.effectiveVisible && <small>skrytá</small>}
      </label>)}
      <div className="category-picker-foot">
        <span>{selected.length ? `Vybrané: ${selected.length}` : 'Nič nevybrané'}</span>
        <button type="button" className="text-btn" onClick={() => setOpen(false)}>Hotovo</button>
      </div>
    </div>}
  </div>
}
